import cloneDeep from "lodash.clonedeep"
import filter from "lodash.filter"
import isEqual from "lodash.isequal"
import sortBy from "lodash.sortby"
import isEmpty from "lodash.isempty"
import uniq from "lodash.uniq"
import uniqBy from "lodash.uniqby"
import merge from "lodash.merge"
import values from "lodash.values"
import debounce from "lodash.debounce"
import find from "lodash.find"
import pick from "lodash.pick"
import omit from "lodash.omit"
import { Metrics } from "@/store/helpers/metrics"
import { Pages } from "@/store/helpers/pages"
import store from "@/store"
import moment from "moment"
import slugify from "slugify"
import { Record as BaseRecord } from "@/models/record"
import { ObjectType } from "@/models/object_type"
import { pluralize, singularize } from "inflection"

class NounHelper {
  static toLabel(name) {
    // Handle camelCase and PascalCase by adding spaces before capital letters
    let label = name.replace(/([a-z])([A-Z])/g, "$1 $2")
    // Replace underscores with spaces
    label = label.replace(/_/g, " ")
    // Convert to title case
    return this.convertCase(label, "title")
  }

  static toSingular(noun, caseOption = "title") {
    if (caseOption === "title") {
      noun = this.toLabel(noun)
    }
    const words = noun.split(" ")
    const singularWords = words.map((word) => singularize(word) || word)
    const singularNoun = singularWords.join(" ")
    return this.convertCase(singularNoun, caseOption)
  }

  static toPlural(noun, caseOption = "title") {
    if (caseOption === "title") {
      noun = this.toLabel(noun)
    }
    const words = noun.split(" ")
    const pluralWords = words.map((word) => {
      return !["all", "those", "that", "this"].includes(word.toLowerCase()) ? pluralize(this.toSingular(word, caseOption)) : word
    })
    const pluralNoun = pluralWords.join(" ")
    return this.convertCase(pluralNoun, caseOption)
  }

  static toBoth(noun, caseOption = "title") {
    if (caseOption === "title") {
      noun = this.toLabel(noun)
    }
    const singular = this.toSingular(noun, caseOption)
    const plural = this.toPlural(singular, caseOption)
    return { singular, plural }
  }

  static convertCase(phrase, caseOption) {
    const words = phrase.split(" ")

    switch (caseOption) {
      case "camel":
        return (
          words[0].toLowerCase() +
          words
            .slice(1)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join("")
        )
      case "upper":
        return phrase.toUpperCase()
      case "lower":
        return phrase.toLowerCase()
      case "capitalize":
        return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase()
      case "snake":
        return words.map((word) => word.toLowerCase()).join("_")
      case "dash":
        return words.map((word) => word.toLowerCase()).join("-")
      default: // title case
        return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ")
    }
  }
}

class Utils extends Metrics {
  static nounHelper = NounHelper
  static pages = Pages
  static metrics = Metrics

  static ObjectType = function (type) {
    return new ObjectType(type)
  }
  static SortedObjectTypes = function () {
    return ObjectType.sortedByMetricCount()
  }

  static debounce = (func, wait = 500, options = { leading: false, trailing: true }) => debounce(func, wait, options)()

  static slugify = function (text, replacement = "_") {
    if (text) {
      return slugify(text, { replacement: replacement, lower: true })
    }
    return ""
  }

  static toLabel = function (text, replacement = "_") {
    if (text) {
      return slugify(text, { replacement: replacement, lower: true })
    }
    return ""
  }

  static replaceLast = (str, pattern, replacement) => {
    const match = typeof pattern === "string" ? pattern : (str.match(new RegExp(pattern.source, "g")) || []).slice(-1)[0]
    if (!match) {
      return str
    }
    const last = str.lastIndexOf(match)
    return last !== -1 ? `${str.slice(0, last)}${replacement}${str.slice(last + match.length)}` : str
  }

  static textOverflows = function (refs, layout_ref) {
    return refs[layout_ref]?.scrollWidth !== refs[layout_ref]?.clientWidth
  }
  static toggleItemFromList = function (list, item, key = null) {
    // toggle item from list
    if (key) {
      let index = list.findIndex((x) => x[key] == item[key])
      if (index > -1) {
        list.splice(index, 1)
      } else {
        list.push(item)
      }
    } else {
      let index = list.findIndex((x) => x == item)
      if (index > -1) {
        list.splice(index, 1)
      } else {
        list.push(item)
      }
    }
    return list
  }

  static Record = function (record) {
    return new BaseRecord(record)
  }

  static getComponentRecord = function (component) {
    return BaseRecord.getComponentRecord(component)
  }

  static getRecordName = function (record) {
    if (this.isUUID(record)) {
      return BaseRecord.from(record)?.name
    }
    if (record) {
      if (record?.name) {
        return record.name
      }
      if (BaseRecord.from(record)?.name) {
        return BaseRecord.from(record)?.name
      }

      if (record?.values) {
        return record.values[
          `${record.type == "contact" ? `${store.state.integration.metrics_mapping.contact_first_name_metric} ${store.state.integration.metrics_mapping.contact_last_name_metric}` : `${store.state.integration.metrics_mapping[`${record.type}_name_metric`]}`}`
        ]
      }
    }
    return null
  }

  static splitCamelCase = function (word) {
    if (word) {
      return word.replace(/([a-z](?=[A-Z]))/g, "$1 ")
    }
    return word
  }

  static formatAsCurrency = (val) => {
    let value = val
    if (typeof val == "string") {
      value = parseInt(val)
    }
    if (value) {
      return value
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")
        .toString()
        .replace(/\.0{2}/, "")
    } else {
      return value
    }
  }

  static formatDecimalAsCurrency = (value, decimal_places) => {
    if (typeof value == "string") {
      let value = parseInt(value)
    }
    if (value) {
      return value
        .toFixed(decimal_places)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")
        .toString()
        .replace(/\.0{2}/, "")
    } else {
      return value
    }
  }
  static humanizeNumber = (val) => {
    if (val === null || val === undefined) {
      return ""
    }

    // Convert to number if string
    const num = typeof val === "string" ? Number(val.replace(/[^0-9.-]+/g, "")) : Number(val)

    if (isNaN(num)) {
      return val
    }

    // Handle different ranges
    if (Math.abs(num) >= 1e9) {
      return (num / 1e9).toFixed(1) + "B"
    } else if (Math.abs(num) >= 1e6) {
      return (num / 1e6).toFixed(1) + "M"
    } else if (Math.abs(num) >= 1e3) {
      return (num / 1e3).toFixed(1) + "K"
    }

    // Format regular numbers
    return this.formatAsCurrency(num).toLocaleString()
  }
  static humanize = (val) => {
    return this.humanizeNumber(val)
  }
  static clone = (value) => cloneDeep(value)
  static find = (value, predicate) => find(value, predicate)
  static pick = (value, predicate) => pick(value, predicate)
  static omit = (value, predicate) => omit(value, predicate)

  static isNil = (value) => value == undefined || value == null
  static isEmpty = (value) =>
    value == "undefined" ||
    value == undefined ||
    value == null ||
    value == "null" ||
    (typeof value == "object" && isEmpty(value)) ||
    utils.isNil(value) ||
    String(value).trim() == "" ||
    (Array.isArray(value) && value?.length == 0)
  static isNotEmpty = (value) => !utils.isEmpty(value)

  static uniqBy = (value, predicate) => uniqBy(value, predicate)
  static uniq = (value) => uniq(value)
  static values = (value) => values(value)
  static orderBy = (list, pred) => sortBy(list, pred)
  static merge = (objA, objB) => merge(objA, objB)
  static groupBy = (list, attr) => {
    return list.reduce((r, a) => {
      r[a[attr]] = r[a[attr]] || []
      r[a[attr]].push(a)
      return r
    }, Object.create(null))
  }
  static filter = (values_list, predicate) => filter(values_list, predicate)
  static filterAndSort = (items_list, filter_term, search_fields = ["label", "value"]) => {
    // Normalize filter term to lower case for case-insensitive comparison
    const normalized_filter_term = filter_term.toLowerCase()

    // Filter items based on the type of elements in items_list
    const filtered_items = items_list.filter((item) => {
      // If item is a string, directly check if it includes the filter term
      if (typeof item === "string") {
        return item.toLowerCase().includes(normalized_filter_term)
      } else if (typeof item === "object" && search_fields) {
        // If item is an object and search_fields are provided, filter based on these fields
        // If search_fields are null or undefined, use all fields of the item for filtering
        const fields_to_search = search_fields.length > 0 ? search_fields : Object.keys(item)
        return fields_to_search.some((field) => item[field] && item[field].toString().toLowerCase().includes(normalized_filter_term))
      }
      return false // Exclude item if it does not match any condition
    })

    // Sort the filtered items with dynamic prioritization
    return filtered_items.sort((a, b) => {
      // Extract string values for comparison, accounting for object and string types
      const strA = typeof a === "string" ? a : a[search_fields.find((field) => a[field])]
      const strB = typeof b === "string" ? b : b[search_fields.find((field) => b[field])]

      // Calculate priority based on the index of filter_term occurrence
      const indexA = strA.toLowerCase().indexOf(normalized_filter_term)
      const indexB = strB.toLowerCase().indexOf(normalized_filter_term)

      // Prioritize items where the filter_term occurs earlier
      if (indexA !== indexB) {
        return indexA - indexB
      }

      // If the occurrence index is the same, or for items without the filter_term, sort alphabetically
      return strA.localeCompare(strB)
    })
  }

  static addItem = (items, item) => {
    items.splice(items.length, 1, item)
    return items
  }
  static getItem = (items, item, attr = "id") => {
    let index = items.findIndex((x) => x[attr] == item[attr])
    return items[index]
  }
  static replaceItem = (items, item, attr = "id", append_if_not_found = true) => {
    let index = items.findIndex((x) => (x ? x[attr] : null) == item[attr])
    if (index == -1 && append_if_not_found) {
      items.push(item)
    } else {
      items.splice(index, 1, item)
    }
    return items
  }
  static replaceItems = (a_items, b_items, attr = "id") => {
    b_items.forEach((item) => {
      let index = a_items.findIndex((x) => (x ? x[attr] : null) == item[attr])
      a_items.splice(index, 1, item)
    })
    return a_items
  }
  static replaceOrAddItem = (items, item, attr = "id", unshift = false) => {
    let index
    if (Array.isArray(attr)) {
      index = items.findIndex((x) => utils.isEqual(utils.pick(x, attr), utils.pick(item, attr)))
    } else {
      index = items.findIndex((x) => x[attr] == item[attr])
    }

    if (unshift && index == -1) {
      items.unshift(item)
    } else {
      if (index == -1) {
        index = items.length
      }
      items.splice(index, 1, item)
    }

    return items
  }
  static replaceIdWithItem = (items, item, id) => {
    if (items.length > 0) {
      let index = items.findIndex((x) => x["id"] == id)
      if (index != -1) {
        items.splice(index, 1, item)
      } else {
        items.push(item)
      }
      return items
    } else {
      return [item]
    }
  }

  static getFirstDayOfMonth(monthYear) {
    const [month, year] = monthYear.split("/").map(Number)
    const firstDay = new Date(year, month - 1, 1)
    return firstDay.toISOString()
  }

  static getStartOfYearISO(year) {
    const firstDay = new Date(year, 0, 1)
    return firstDay.toISOString()
  }

  static getStartOfQuarterISO(quarterYear) {
    const [quarter, year] = quarterYear.split("/").map((part, index) => (index === 1 ? Number(part) : part))
    let startMonth
    switch (quarter) {
      case "Q1":
        startMonth = 0 // January
        break
      case "Q2":
        startMonth = 3 // April
        break
      case "Q3":
        startMonth = 6 // July
        break
      case "Q4":
        startMonth = 9 // October
        break
      default:
        throw new Error("Invalid quarter format. Use Q1, Q2, Q3, or Q4.")
    }
    const firstDay = new Date(year, startMonth, 1)
    return firstDay.toISOString()
  }
  static scoreSearch(q, items, labelAttrs = ["label"]) {
    const getItemLabel = (item, labelAttrs) => {
      if (typeof item === "object" && item) {
        // Convert single attribute to array for consistent handling
        const attrs = Array.isArray(labelAttrs) ? labelAttrs : [labelAttrs]
        // Try each attribute and return first non-empty value
        for (const attr of attrs) {
          if (item[attr]) {
            return item[attr]
          }
        }
      }
      return item
    }

    if (!q || q.trim() === "") {
      return items
    }

    const query = q.toLowerCase().trim()

    // Calculate relevance score for each item
    return items
      .map((item) => {
        const label = getItemLabel(item, labelAttrs)?.toLowerCase() || ""
        if (!label.includes(query)) {
          return null // Filter out non-matching items
        }

        // Calculate relevance score
        let score = 0

        // Exact match gets highest score
        if (label === query) {
          score += 100
        }

        // Starts with query gets high score
        if (label.startsWith(query)) {
          score += 50
        }

        // Word boundary match gets medium score
        const wordBoundaryMatch = new RegExp(`\\b${query}`, "i").test(label)
        if (wordBoundaryMatch) {
          score += 25
        }

        // Position of match affects score (earlier = better)
        const position = label.indexOf(query)
        if (position >= 0) {
          score += (100 - Math.min(position, 100)) * 0.1
        }

        // Ratio of query length to total length (higher ratio = better match)
        score += (query.length / label.length) * 10

        return { item, score }
      })
      .filter(Boolean) // Remove null items
      .sort((a, b) => b.score - a.score) // Sort by score (descending)
      .map((result) => result.item) // Return just the items
  }
  static getStartOfWeekISO(weekYear) {
    const [week, year] = weekYear.split("/").map(Number)
    const jan1 = new Date(year, 0, 1)
    const dayOfWeekJan1 = jan1.getDay()
    const daysUntilFirstMonday = dayOfWeekJan1 <= 4 ? 1 - dayOfWeekJan1 : 8 - dayOfWeekJan1
    const startOfWeek = new Date(year, 0, 1 + daysUntilFirstMonday + (week - 1) * 7)
    return startOfWeek.toISOString()
  }

  static convertFiltersFromLocalDateToUtc = (filter, truncate_by) => {
    if (filter && !Array.isArray(filter)) {
      const filter_list = filter.split(" ")
      filter_list.forEach((item, index) => {
        if (
          this.getMetricByReferenceName(item) &&
          (this.getMetricByReferenceName(item).data_type == "Date" || this.getMetricByReferenceName(item).data_type == "DateTime") &&
          !filter_list[index + 2].includes("today") &&
          !filter_list[index + 2].includes(",") &&
          filter_list[index + 2] !== "None"
        ) {
          const cleanedDate = filter_list[index + 2].replace(/^'|'$/g, "")
          if (truncate_by == "month") {
            const firstDay = this.getFirstDayOfMonth(cleanedDate)
            filter_list.splice(index + 2, 1, `DateMonth('${firstDay}')`)
          } else if (truncate_by == "year") {
            const firstDay = this.getStartOfYearISO(cleanedDate)
            filter_list.splice(index + 2, 1, `DateYear('${firstDay}')`)
          } else if (truncate_by == "quarter") {
            const firstDay = this.getStartOfQuarterISO(cleanedDate)
            filter_list.splice(index + 2, 1, `DateQuarter('${firstDay}')`)
          } else if (truncate_by == "week") {
            const firstDay = this.getStartOfWeekISO(cleanedDate)
            filter_list.splice(index + 2, 1, `DateWeek('${firstDay}')`)
          } else {
            const offset = new Date().getTimezoneOffset()
            const date_calc = new Date(`${filter_list[index + 2].replaceAll("'", "")}T17:00:00`)
            date_calc.setHours(date_calc.getHours() + offset / 60)
            const date_value_calc = `${date_calc.getFullYear()}-${
              date_calc.getMonth() + 1 < 10 ? `0${date_calc.getMonth() + 1}` : date_calc.getMonth() + 1
            }-${date_calc.getDate() < 10 ? `0${date_calc.getDate()}` : date_calc.getDate()}`
            filter_list.splice(index + 2, 1, `'${date_value_calc}'`)
          }
        }
      })
      return filter_list.join(" ")
    }
  }

  static containsDateTimeInfo(dateString) {
    const date = moment(dateString)
    if (!date.isValid()) {
      return false
    }
    return dateString.length > 10
  }

  static convertDateStringFromLocalDateToUtc = (date_string, date_type = "DateTime") => {
    let date = moment(date_string)
    if (!date.isValid()) {
      return date_string
    }

    if (date_type == "DateTime") {
      return date.toISOString()
    } else if (date_type == "Date") {
      // send as is for date only (already utc)
      // center to 12:00:00
      date.set({ hour: 12, minute: 30, second: 30, millisecond: 500 })
      return date.format("YYYY-MM-DDTHH:mm:ss")
    } else {
      // time return as is
      return date_string
    }
  }

  static removeItem = (items, item, attr = "id") => {
    let index
    if (typeof item != "object") {
      if ((typeof item == "string" || typeof item == "number") && attr != null) {
        index = items.findIndex((x) => x[attr] == item)
      } else {
        index = items.indexOf(item)
      }
    } else {
      index = items.findIndex((x) => x[attr] == item[attr])
    }
    if (index != -1) {
      items.splice(index, 1)
    }
    return items
  }

  static truncateNumber = (value) => {
    if (value >= 1000_000_000_000_000_000) {
      return (value / 1000_000_000_000_000_000).toFixed(1).replace(/\.0$/, "") + "E"
    }
    if (value >= 1000_000_000_000_000) {
      return (value / 1000_000_000_000_000).toFixed(1).replace(/\.0$/, "") + "P"
    }
    if (value >= 1000_000_000_000) {
      return (value / 1000_000_000_000).toFixed(1).replace(/\.0$/, "") + "T"
    }
    if (value >= 1000_000_000) {
      return (value / 1000_000_000).toFixed(1).replace(/\.0$/, "") + "B"
    }
    if (value >= 1000_000) {
      return (value / 1000_000).toFixed(1).replace(/\.0$/, "") + "M"
    }
    if (value >= 1000) {
      return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K"
    } else {
      return value
    }
  }
  /* Given two objects will return if equal
    if strict is true then it compares the order of keys as well
  */
  static isEqual(ob1, ob2, strict = false, excluded_keys = [], included_keys = null) {
    if (included_keys) {
      ob1 = utils.pick(ob1, included_keys)
      ob2 = utils.pick(ob2, included_keys)
    } else if (excluded_keys.length > 0) {
      ob1 = utils.omit(ob1, excluded_keys)
      ob2 = utils.omit(ob2, excluded_keys)
    }
    if (strict) {
      return JSON.stringify(ob1) == JSON.stringify(ob2)
    } else {
      return isEqual(ob1, ob2)
    }
  }
  /* Sort list ob objects based on list of ids */
  static sortObjectsList(list, keys_order, key = null) {
    return sortBy(list, (item) => (key ? keys_order.indexOf(item[key]) : keys_order.indexOf(item)))
  }
  /* Given a list of objects will
     Return list of values of key 'key' 
  */
  static getKeyValues(array, key = "id") {
    return array.map((x) => x[key])
  }
  static generateDayWiseTimeSeries(baseval, count, yrange) {
    var i = 0
    var series = []
    while (i < count) {
      var y = Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min

      series.push([baseval, y])
      baseval += 86400000
      i++
    }
    return series
  }
  static setRandomKey(el, attr = "key") {
    if (el) {
      el[attr] = Math.random().toString().substring(2, 20)
    }
    return el
  }
  static generateRandomKey(prefix = "key-", length = 18) {
    return (
      prefix +
      Math.random()
        .toString()
        .substring(2, 2 + length)
    )
  }
  static randomIntFromInterval(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
  static isNumber(v) {
    try {
      if (parseFloat(String(v).replace(",", "."))) {
        return true
      }
    } catch (error) {
      return false
    }
  }
  static capitalize(text) {
    let all_words_capital = []
    const all_words = text?.split(" ")
    all_words?.forEach((word) => {
      all_words_capital.push(word.charAt(0).toUpperCase() + word.slice(1))
    })
    return all_words_capital.join(" ")
  }
  static removeLastChars(text, no) {
    const chars_to_remove = no ? no : 1
    return text.slice(0, text.length - chars_to_remove)
  }
  static percentagify(list) {
    let total = list.reduce((a, b) => a + b, 0)
    return list.map((x) => Math.ceil((x / total) * 100))
  }
  static escapeApostrophe(text) {
    return text.replace(/'/g, "\\'")
  }
  static convertToFormData(dict) {
    let formData = new FormData()
    Object.entries(dict).forEach(([key, value]) => {
      if (typeof value === "object") {
        Object.entries(value).forEach(([subKey, subValue]) => {
          formData.append(`${key}[${subKey}]`, subValue)
        })
      } else if (Array.isArray(value) && value.length > 0) {
        if (typeof value[0] === "object") {
          formData.set(`${key}`, [])
          value.forEach((val, index) => {
            formData.append(`${key}[${index}]`, val)
          })
        } else {
          formData.append(key, value)
        }
      } else {
        formData.append(key, value)
      }
    })
    return formData
  }

  static makePlural(phrase) {
    if (!phrase) {
      return phrase
    }

    const words = phrase.split(" ")
    const lastWord = words[words.length - 1]

    // Don't pluralize if last word is a single uppercase letter (e.g., "A", "B")
    if (/^[A-Z]$/.test(lastWord)) {
      return phrase
    }

    let pluralLastWord = lastWord

    if (lastWord.toLowerCase().includes("matrix")) {
      pluralLastWord = lastWord.replace(/rix$/i, "rices")
    } else if (lastWord.endsWith("y") && !lastWord.toLowerCase().endsWith("journey") && !lastWord.endsWith("ies")) {
      pluralLastWord = lastWord.slice(0, -1) + "ies"
    } else if (!lastWord.endsWith("s") && !lastWord.endsWith("es")) {
      pluralLastWord = lastWord + "s"
    }

    words[words.length - 1] = pluralLastWord
    return words.join(" ")
  }

  /**
   * extract value
   * @param {[Object,String]} value - value object has record id as key with value inside, value could be string or array.
   * @param {String} empty_value - default value if there is value for this record is not available.
   * @param {Object} route - Current route passed.
   */
  static extractValue(value, empty_value, route) {
    if (typeof route == "object") {
      if (typeof value == "object" && !Array.isArray(value) && value !== null) {
        const connect_to_string = `${route.params?.object_type}>${route.params.cid}`
        if (value[connect_to_string]) {
          return value[connect_to_string]
        } else {
          return empty_value
        }
      } else {
        return value
      }
    }
  }

  /**
   * extract url
   * @param {[Object,String]} url - url object has record id as key with url string inside.
   * @param {Object} route - Current route passed.
   */
  static extractUrl(url, route) {
    if (url) {
      // if the url start with http return it
      if (url.toString().startsWith("http")) {
        return url
      }
      let url_string = ""
      if (typeof url == "object") {
        const page_identifier_string = `${route.params?.object_type}>${route.params.cid}`
        if (url[page_identifier_string]) {
          url_string = url[page_identifier_string]
        } else {
          url_string = "N/A"
        }
      } else {
        url_string = url
      }

      if (import.meta.env.VITE_NODE_ENV == "development" && url_string !== "N/A" && url_string) {
        if (!url_string.includes(import.meta.env.VITE_BASE_API_URL)) {
          url_string = import.meta.env.VITE_BASE_API_URL + url_string
        }
      }
      return url_string
    }
  }
  static isUUID(str) {
    try {
      return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str)
    } catch (error) {
      return false
    }
  }
  static isNilUUID(str) {
    return str == "00000000-0000-0000-0000-000000000000"
  }
  static getOptionMetricReference(object_type, option = "name", returned_field = "reference_name", return_default = true) {
    let option_metric = this.getMetricById(
      store.state.integration.platform_metric_mappings.find((m) => m.option == option && m.object_type == object_type)?.metric
    )
    if (!option_metric) {
      option_metric = store.state.integration.metrics_mapping[object_type + "_" + option + "_metric"]
    }
    if (!option_metric) {
      if (return_default) {
        let metric_ref = object_type + "_" + option
        if (returned_field == "id") {
          let resp = store.state.metrics.filter(
            (m) => m.data_type == "String" && m.object_type == object_type && m.reference_name.includes(metric_ref)
          )[0]
          if (resp) {
            return resp.id
          } else {
            return store.state.metrics.filter((m) => m.data_type == "String" && m.object_type == object_type)[0]?.id
          }
        } else {
          return metric_ref
        }
      } else {
        return ""
      }
    }
    if (returned_field) {
      return option_metric[returned_field]
    } else {
      return option_metric
    }
  }
  static isEmail(email) {
    if (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }
    return false
  }
  static extractContactInfoFromInputWord(word, company_domain) {
    // word can be email or a name of first and last name
    // if email extract the first name and last name from the email first part and spliting by . and _ and -

    let first_name = ""
    let last_name = ""
    let email = ""
    if (this.isEmail(word)) {
      email = word
      let email_parts = word.split("@")[0].split(/[_\-.]/)
      first_name = email_parts[0]
      last_name = email_parts[1]
    } else {
      let name_parts = word?.split(" ")
      first_name = name_parts?.length ? name_parts[0] : ""
      last_name = name_parts?.length ? name_parts[1] : ""
      email = company_domain ? `${first_name}.${last_name}@${company_domain}` : ""
    }

    return { first_name, last_name, email }
  }
  static generateRandomString(length) {
    let result = ""
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }
}

export const utils = Utils
export const Record = BaseRecord
export const nounHelper = NounHelper
